<template>
  <div class="lt-bar">
    <!-- Left: selection gestures, then whatever actions the host page supplies. -->
    <div class="lt-barLeft">
      <div v-if="!hideSelect" ref="selectMenuEl" class="lt-masterSelect">
        <input
          type="checkbox"
          :checked="pageFullySelected"
          :indeterminate.prop="someSelected && !pageFullySelected"
          :aria-label="pageFullySelected ? 'Deselect all' : 'Select page'"
          title="Select page"
          @change="$emit('toggle-page')"
        />
        <button
          type="button"
          class="lt-masterCaret"
          aria-label="Selection options"
          :aria-expanded="selectMenuOpen"
          aria-haspopup="menu"
          @click="selectMenuOpen = !selectMenuOpen"
        >
          <AppIcon name="chevron_down" :size="12" />
        </button>
        <div v-if="selectMenuOpen" class="lt-selectMenu" role="menu">
          <button type="button" role="menuitem" class="lt-selectMenuItem" @click="pick('page')">
            All on this page
          </button>
          <button type="button" role="menuitem" class="lt-selectMenuItem" @click="pick('none')">
            None
          </button>
        </div>
      </div>

      <span v-if="someSelected" class="lt-count">{{ selectedCount }} selected</span>

      <div v-if="someSelected" class="lt-actions">
        <slot name="actions" />
      </div>
      <div v-else class="filters">
        <slot name="filters" />
      </div>
    </div>

    <!-- Right: general-actions icons, then sort menu behind the range label, then the pager. -->
    <div class="lt-barRight">
      <template v-if="$slots['general-actions']">
        <div class="lt-generalActions">
          <slot name="general-actions" />
        </div>
        <div class="lt-separator" />
      </template>

      <SortMenu :model-value="sort ?? ''" :options="sortOptions" @update:model-value="(v) => $emit('update:sort', v)">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="lt-rangeBtn"
            :aria-expanded="open"
            aria-haspopup="menu"
            title="Change sort order"
            @click="toggle"
          >
            {{ rangeLabel }}
          </button>
        </template>
      </SortMenu>

      <button
        type="button"
        class="lt-pagerBtn"
        :disabled="!canPrev"
        title="Previous page"
        @click="$emit('prev')"
      >
        ‹
      </button>
      <button
        type="button"
        class="lt-pagerBtn"
        :disabled="!canNext"
        title="Next page"
        @click="$emit('next')"
      >
        ›
      </button>
    </div>
  </div>

  <!-- Escalation banner: only once the whole page is selected, so the offer to
       widen the selection appears exactly when it's the natural next step. -->
  <div v-if="pageFullySelected && escalationAvailable" class="lt-escalation">
    <template v-if="allMatchingSelected">
      All <strong>{{ selectedCount }}</strong> {{ noun }} matching this filter are selected.
      <button type="button" class="lt-escalationLink" @click="$emit('select-none')">Clear selection</button>
    </template>
    <template v-else>
      All <strong>{{ pageSize ?? 0 }}</strong> {{ noun }} on this page are selected.
      <button
        type="button"
        class="lt-escalationLink"
        :disabled="selectingAll"
        @click="$emit('select-all-matching')"
      >
        {{ selectingAll ? 'Selecting…' : `Select all ${escalationTarget} matching this filter` }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import SortMenu from './SortMenu.vue'

/**
 * The list header bar: selection gestures on the left, sort + paging on the
 * right, escalation banner underneath.
 *
 * Extracted from the Actions page so every paged list in the app shares one
 * bar. What differs per page — the bulk-action buttons, the inline filters, the
 * sort vocabulary — arrives as slots and props; the selection and paging
 * mechanics do not vary and live here.
 *
 * The `actions` and `filters` slots occupy the same spot and swap on selection:
 * with nothing selected the filters are what you want in reach, and the moment
 * you select something the actions take over that space.
 */

export type SortOption = { value: string; label: string }

const props = defineProps<{
  /** Rows on the current page, for the master checkbox and escalation copy. */
  pageSize?: number
  selectedCount?: number
  pageFullySelected?: boolean
  /** Total matching the filter, for the escalation offer. */
  total?: number
  /** Cap on how many "select all" can reach. */
  selectAllMax?: number
  selectingAll?: boolean
  rangeLabel: string
  sort?: string
  sortOptions: SortOption[]
  canPrev?: boolean
  canNext?: boolean
  /** Plural noun for the escalation banner, e.g. "candidates". */
  noun?: string
  hideSelect?: boolean
}>()

const emit = defineEmits<{
  'toggle-page': []
  'select-all-matching': []
  'select-none': []
  prev: []
  next: []
  'update:sort': [string]
}>()

const selectMenuOpen = ref(false)
const selectMenuEl = ref<HTMLElement | null>(null)

const someSelected = computed(() => (props.selectedCount ?? 0) > 0)
const noun = computed(() => props.noun || 'rows')

const escalationTarget = computed(() => Math.min(props.total ?? 0, props.selectAllMax ?? Infinity))
const escalationAvailable = computed(() => (props.total ?? 0) > (props.pageSize ?? 0))
const allMatchingSelected = computed(() => (props.selectedCount ?? 0) >= escalationTarget.value)

function pick(which: 'page' | 'none') {
  selectMenuOpen.value = false
  if (which === 'none') emit('select-none')
  else if (!props.pageFullySelected) emit('toggle-page')
}

function onPointerDown(e: PointerEvent) {
  const t = e.target
  if (!(t instanceof Node)) return
  if (selectMenuOpen.value && selectMenuEl.value && !selectMenuEl.value.contains(t)) selectMenuOpen.value = false
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown))
</script>

<style scoped>
/* Everything else the template uses (.lt-bar, .lt-masterSelect, .lt-rangeBtn,
   .lt-pagerBtn, .lt-escalation, etc.) lives in the shared "List toolbar
   primitive" section of ../../styles.css, so a restyle there reaches every
   paged list at once. Only .filters — unique to this component's slot
   layout — stays local. */
.filters { display: flex; align-items: center; gap: var(--space-2); min-width: 0; }

.lt-generalActions { display: flex; align-items: center; gap: var(--space-1); }
.lt-separator { width: 1px; height: 20px; background: var(--color-border-subtle); }
</style>
